import { useQuery } from '@tanstack/react-query'
import {
  ReportsApi,
  ReportStatusEnum,
  SearchReportsRequestBody,
  TraceKeyResponseTypeEnum,
} from '@ulysses-inc/harami_api_client'
import { addDays } from 'date-fns'
import { queryKeys } from 'src/features/reports/listBeta/api/queryKeys'
import { ReportListFilters } from 'src/features/reports/listBetaFilters/types'
import { baseClient } from 'src/state/middleware/saga/baseClient'
import { handleHTTPError } from 'src/state/middleware/saga/handleHttpError'
import { parseDateString } from 'src/util/datetime/parseDateString'
import invariant from 'tiny-invariant'

export type Report = {
  assigneeName: string
  hasSomeDeviatedAnswer: boolean
  id: number
  isAudit: boolean
  name: string
  placeName: string
  reportDate: Date
  updatedAt: Date
  uuid: string
  score: string
  status:
    | 'inCompleted'
    | 'approvalPending'
    | 'approvalRemand'
    | 'completed'
    | 'unknown'
  traceTags: string[]
}

type ReportsQueryResult = {
  pagination: {
    prevPage: boolean
    nextPage: boolean
  }
  reports: Report[]
}

/**
 * 絞り込み条件をAPIに投げられる形のオブジェクトに変換する
 */
export const toApiFilters = (
  filters: ReportListFilters,
): SearchReportsRequestBody => {
  const result: SearchReportsRequestBody = {}

  const {
    dateFrom,
    dateTo,
    deviatedAnswer,
    employeeNameOrCode,
    placeNodeUuids,
    reportName,
    statuses,
    traceKeyConditions,
  } = filters

  if (dateFrom && dateTo) {
    const dateTimeFrom = parseDateString(dateFrom)
    const dateTimeTo = parseDateString(dateTo)
    invariant(dateTimeFrom && dateTimeTo)
    result.reportDate = {
      $gte: dateTimeFrom,
      // 例えば日付の範囲指定が「2021-01-01から2021-01-02」だった場合、
      // 終点は2021-01-03T00:00:00になるべきであるため、1日足しておく。
      $lt: addDays(dateTimeTo, 1),
    }
  }

  if (deviatedAnswer && deviatedAnswer.length > 0) {
    const deviatedAnswerForApi = deviatedAnswer.map(deviatedAnswerFilter => {
      switch (deviatedAnswerFilter) {
        case 'absent':
          return false
        case 'present':
          return true
      }
    })
    result.isInvalid = { $in: deviatedAnswerForApi }
  }

  if (employeeNameOrCode) {
    result.answer ??= {}
    result.answer.employee = { $like: employeeNameOrCode }
  }

  if (placeNodeUuids.length > 0) {
    result.placeNodeId = { $in: placeNodeUuids }
  }

  if (reportName) {
    result.reportName = { $like: reportName }
  }

  if (statuses && statuses.length > 0) {
    const statusesForApi = statuses.map(status => {
      switch (status) {
        case 'inCompleted':
          return ReportStatusEnum.INCOMPLETE
        case 'approvalPending':
          return ReportStatusEnum.APPROVAL_PENDING
        case 'approvalRemand':
          return ReportStatusEnum.APPROVAL_REMAND
        case 'completed':
          return ReportStatusEnum.COMPLETE
      }
    })
    result.status = { $in: statusesForApi }
  }

  if (traceKeyConditions && traceKeyConditions.length > 0) {
    // 選択した終了日を含めるため、Date型のtraceKeyConditionsの終了日に+1日を追加
    const conditionsForApi = traceKeyConditions.map(condition => {
      switch (condition.responseType) {
        case TraceKeyResponseTypeEnum.Date:
          return condition.datetime
            ? {
                ...condition,
                datetime: {
                  ...condition.datetime,
                  lessThan: addDays(condition.datetime.lessThan, 1),
                },
              }
            : condition
        case TraceKeyResponseTypeEnum.ResponseSet:
        case TraceKeyResponseTypeEnum.Text:
        default:
          return condition
      }
    })

    result.traceKey = {
      traceKeyConditions: conditionsForApi,
    }
  }

  return result
}

export const useQueryReports = (filters: ReportListFilters) => {
  return useQuery({
    queryKey: queryKeys.reports(filters),
    queryFn: async () => {
      return await baseClient
        .getApi(ReportsApi)
        .searchReportsV2({
          page: filters.page,
          pageSize: filters.pageSize,
          searchReportsRequestBody: toApiFilters(filters),
        })
        .catch(handleHTTPError)
    },
    select: ({ reports, pagination }): ReportsQueryResult => {
      return {
        pagination: pagination,
        reports: reports.map(report => {
          const convertStatus = (status: ReportStatusEnum) => {
            switch (status) {
              case ReportStatusEnum.INCOMPLETE:
                return 'inCompleted'
              case ReportStatusEnum.APPROVAL_PENDING:
                return 'approvalPending'
              case ReportStatusEnum.APPROVAL_REMAND:
                return 'approvalRemand'
              case ReportStatusEnum.COMPLETE:
                return 'completed'
              default:
                return 'unknown'
            }
          }
          return {
            // 記録者名は principal.name (User はユーザー名、共用端末は端末名)。未解決は空表示
            assigneeName: report.principal?.name ?? '',
            // isInvalidが0なら逸脱回答がない、1なら逸脱回答がある、ということらしい
            hasSomeDeviatedAnswer: !!report.hasSomeDeviatedAnswer,
            id: report.id,
            isAudit: report.isAudit || false,
            name: report.name,
            placeName: report.placeName || '',
            reportDate: report.reportDate,
            score: report.isShowScore ? `${report.score}%` : '-',
            status: convertStatus(report.status),
            updatedAt: report.updatedAt,
            uuid: report.uuid,
            traceTags: report.traceTags.map(tag => tag.value),
          }
        }),
      }
    },
  })
}
